import {omneticApi} from '@dms/api/base';

import {
  CreateQuickPresetApiArg,
  CreateQuickPresetApiResponse,
  DeleteQuickPresetApiArg,
  DeleteQuickPresetApiResponse,
  EditQuickPresetApiArg,
  EditQuickPresetApiResponse,
  GetDescriptionGeneratorConfigApiArg,
  GetDescriptionGeneratorConfigApiResponse,
  GetQuickPresetApiArg,
  GetQuickPresetApiResponse,
  GetQuickPresetsApiResponse,
  MoveDownQuickPresetApiArg,
  MoveDownQuickPresetApiResponse,
  MoveUpQuickPresetApiArg,
  MoveUpQuickPresetApiResponse,
  UpdateDescriptionGeneratorConfigApiArg,
  UpdateDescriptionGeneratorConfigApiResponse,
} from './types';

export const publishingDescriptionGeneratorApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getDescriptionGeneratorConfig: build.query<
      GetDescriptionGeneratorConfigApiResponse,
      GetDescriptionGeneratorConfigApiArg
    >({
      query: () => ({
        url: `/dms/v1/sales/description-generator/config?legacyLength=0`,
      }),
    }),
    updateDescriptionGeneratorConfig: build.mutation<
      UpdateDescriptionGeneratorConfigApiResponse,
      UpdateDescriptionGeneratorConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/config`,
        method: 'PUT',
        body: queryArg.body,
      }),
      onQueryStarted: async (queryArg, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          publishingDescriptionGeneratorApi.util.updateQueryData(
            'getDescriptionGeneratorConfig',
            {},
            (draft) => {
              Object.assign(draft, queryArg.body);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getQuickPresets: build.query<GetQuickPresetsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/sales/description-generator/quick-preset`,
      }),
      providesTags: (result) =>
        result ? result.map((preset) => ({type: 'QuickPreset', id: preset.id})) : ['QuickPreset'],
    }),
    getQuickPreset: build.query<GetQuickPresetApiResponse, GetQuickPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/quick-preset/${queryArg.id}`,
      }),
      providesTags: (result, error, arg) => [{type: 'QuickPreset', id: arg.id}],
    }),
    createQuickPreset: build.mutation<CreateQuickPresetApiResponse, CreateQuickPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/quick-preset`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: ['QuickPreset'],
    }),
    editQuickPreset: build.mutation<EditQuickPresetApiResponse, EditQuickPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/quick-preset/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      onQueryStarted: async (queryArg, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          publishingDescriptionGeneratorApi.util.updateQueryData(
            'getQuickPreset',
            {id: queryArg.id},
            (draft) => {
              draft.name = queryArg.body.name;
              draft.prompt = queryArg.body.prompt;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, arg) => [{type: 'QuickPreset', id: arg.id}],
    }),
    deleteQuickPreset: build.mutation<DeleteQuickPresetApiResponse, DeleteQuickPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/quick-preset/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{type: 'QuickPreset', id: arg.id}],
    }),
    moveUpQuickPreset: build.mutation<MoveUpQuickPresetApiResponse, MoveUpQuickPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/quick-preset/${queryArg.id}/move-up`,
        method: 'PUT',
      }),
      invalidatesTags: ['QuickPreset'],
    }),
    moveDownQuickPreset: build.mutation<MoveDownQuickPresetApiResponse, MoveDownQuickPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/description-generator/quick-preset/${queryArg.id}/move-down`,
        method: 'PUT',
      }),
      invalidatesTags: ['QuickPreset'],
    }),
  }),
});

export const {
  useGetDescriptionGeneratorConfigQuery,
  useUpdateDescriptionGeneratorConfigMutation,
  useGetQuickPresetsQuery,
  useGetQuickPresetQuery,
  useCreateQuickPresetMutation,
  useEditQuickPresetMutation,
  useDeleteQuickPresetMutation,
  useMoveUpQuickPresetMutation,
  useMoveDownQuickPresetMutation,
} = publishingDescriptionGeneratorApi;
